import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { usePermission } from '../../hooks/usePermission';
import { money } from '../../utils/format';
import { Drawer, Field, PhoneInput, TextInput } from '../../components/ui/Form';
import { ErrorNotice } from '../../components/ui';

interface Customer {
  id: string;
  code: string;
  name: string;
  phone: string | null;
  credit_limit: string;
  outstanding?: string;
}

/**
 * Choosing who the bill is for, at the counter.
 *
 * A walk-in needs nobody, so blank is the default and the fast path. When a
 * regular is buying, they are found by name or phone as you type — never by an
 * id nobody has memorised. And when somebody new is standing there, they are
 * added in a panel that opens over the bill and closes back onto it, because
 * making a cashier leave a half-built invoice to create a customer is how a
 * sale gets lost.
 */
export function CustomerSelect({
  value,
  onChange,
  placeholder = 'Walk-in — or search a name or phone',
}: {
  value: { id: string; name: string } | null;
  onChange: (customer: { id: string; name: string } | null) => void;
  placeholder?: string;
}) {
  const { can } = usePermission();
  const client = useQueryClient();
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);

  const { data } = useQuery({
    queryKey: ['customers', term],
    queryFn: () => endpoints.parties.customers(term || undefined),
    // Opening the box shows who the shop already knows; typing narrows it.
    // Waiting for a search term meant a counter with two hundred customers
    // looked like a counter with none.
    enabled: open,
  });

  const matches = ((data?.data ?? []) as unknown as Customer[]).slice(0, 6);

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-gold-300 bg-gold-50 px-3 py-2">
        <span className="text-sm font-medium">{value.name}</span>
        <button className="ml-auto text-2xs text-ink-soft underline" onClick={() => onChange(null)}>
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        className="field w-full"
        placeholder={placeholder}
        value={term}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
      />

      {open ? (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-silver-100 bg-surface shadow-pop">
          {matches.map((c) => (
            <button
              key={c.id}
              className="flex w-full items-center gap-2 border-b border-silver-100/70 px-3 py-2 text-left text-sm last:border-0 hover:bg-gold-50"
              onClick={() => {
                onChange({ id: c.id, name: c.name });
                setOpen(false);
                setTerm('');
              }}
            >
              <span className="font-medium">{c.name}</span>
              {c.phone ? <span className="num text-2xs text-ink-soft">{c.phone}</span> : null}
              {Number(c.outstanding ?? 0) > 0 ? (
                <span className="num ml-auto text-2xs text-danger">{money(c.outstanding)} due</span>
              ) : null}
            </button>
          ))}

          {term.length === 0 && matches.length > 0 ? (
            <p className="px-3 pb-1 pt-2 text-2xs uppercase tracking-wide text-ink-soft">Recent customers</p>
          ) : null}
          {term.length > 0 && matches.length === 0 ? (
            <p className="px-3 py-2 text-2xs text-ink-soft">Nobody by that name yet.</p>
          ) : null}

          {can('customer.create') ? (
            <button
              className="w-full bg-silver-50 px-3 py-2 text-left text-sm font-medium text-gold-700 hover:bg-gold-50"
              onClick={() => {
                setAdding(true);
                setOpen(false);
              }}
            >
              + Add a new customer
            </button>
          ) : null}
        </div>
      ) : null}

      {adding ? (
        <AddCustomer
          initialName={term}
          onClose={() => setAdding(false)}
          onAdded={(c) => {
            onChange(c);
            setAdding(false);
            setTerm('');
            void client.invalidateQueries({ queryKey: ['customers'] });
          }}
        />
      ) : null}
    </div>
  );
}

/** The smallest customer form that still captures what a bill needs. */
function AddCustomer({
  initialName,
  onClose,
  onAdded,
}: {
  initialName: string;
  onClose: () => void;
  onAdded: (c: { id: string; name: string }) => void;
}) {
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<unknown>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string; name: string }>('/customers', {
        name,
        ...(phone ? { phone } : {}),
      }),
    onSuccess: onAdded,
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (name.trim()) create.mutate();
  };

  return (
    <Drawer
      title="Add a customer"
      subtitle="Just a name is enough. The rest can be filled in later from Customers."
      onClose={onClose}
      footer={
        <button className="btn-primary" disabled={!name.trim() || create.isPending} onClick={submit}>
          {create.isPending ? 'Adding…' : 'Add and use'}
        </button>
      }
    >
      <ErrorNotice error={error} />
      <form autoComplete="off" onSubmit={submit} className="space-y-3">
        <Field label="Name" required>
          <TextInput value={name} onChange={setName} maxLength={200} autoFocus />
        </Field>
        <Field label="Mobile" hint="So the bill can be sent to them later.">
          <PhoneInput value={phone} onChange={setPhone} />
        </Field>
      </form>
    </Drawer>
  );
}
